import { memo } from 'react';
import { format, isWithinInterval, addDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { parseLocalDate, isOverdue as checkOverdue } from '../../utils/dates';

const ProjectCard = ({ project, onEdit }) => {
  const { removeProject } = useApp();

  const targetDate = project.targetDate ? parseLocalDate(project.targetDate) : null;
  const isOverdue = targetDate && checkOverdue(targetDate) && project.status !== 'completed';
  const isDueSoon = targetDate && !isOverdue && isWithinInterval(targetDate, {
    start: new Date(),
    end: addDays(new Date(), 30),
  });

  return (
    <div className={`card project-card${isOverdue ? ' overdue' : ''}`}>
      {/* Clickable body — navigates to detail without editing */}
      <Link to={`/projects/${project._id}`} className="project-card-body">
        <div className="card-header">
          <h3>{project.title}</h3>
          <div className="badges">
            <span className={`badge badge-priority-${project.priority}`}>{project.priority}</span>
            <span className={`badge badge-status-${project.status}`}>{project.status.replace('-', ' ')}</span>
          </div>
        </div>
        {project.description && <p className="card-description">{project.description}</p>}
        <div className="card-meta">
          <span className="badge" style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
            {project.category}
          </span>
          {project.estimatedCost != null && (
            <span style={{ fontWeight: 600, color: '#1e293b' }}>
              ${project.estimatedCost.toLocaleString()}
              {project.actualCost != null && (
                <span style={{ fontWeight: 400, color: 'var(--muted)', fontSize: '0.82rem' }}>
                  {' '}/ ${project.actualCost.toLocaleString()} actual
                </span>
              )}
            </span>
          )}
        </div>
        {targetDate && (
          <p className={`card-date${isOverdue ? ' text-danger' : isDueSoon ? ' text-warning' : ''}`}>
            {isOverdue ? '⚠ Overdue — ' : isDueSoon ? '⏰ Due soon — ' : '📅 '}
            Target: {format(targetDate, 'MMM d, yyyy')}
          </p>
        )}
        {project.estimatedDuration && (
          <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.25rem' }}>
            Est. duration: {project.estimatedDuration}
          </p>
        )}
        {(project.materials?.length > 0 || project.tools?.length > 0) && (
          <p style={{ fontSize: '0.78rem', color: 'var(--primary)', marginTop: '0.35rem', fontWeight: 500 }}>
            {project.materials?.length > 0 && `${project.materials.length} materials`}
            {project.materials?.length > 0 && project.tools?.length > 0 && ' · '}
            {project.tools?.length > 0 && `${project.tools.length} tools`}
            {' — view plan →'}
          </p>
        )}
        {/* Step progress bar */}
        {project.steps?.length > 0 && project.status === 'in-progress' && (() => {
          const total = project.steps.length;
          const done = project.completedSteps?.length || 0;
          const pct = Math.round((done / total) * 100);
          return (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.2rem' }}>
                <span>Progress</span><span>{done}/{total} steps</span>
              </div>
              <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: 'var(--primary)', borderRadius: '3px', transition: 'width 0.3s' }} />
              </div>
            </div>
          );
        })()}
        {/* ROI chips */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
          {project.professionalCost != null && project.estimatedCost != null && (
            <span style={{ fontSize: '0.73rem', background: '#dcfce7', color: '#166534', padding: '0.1rem 0.45rem', borderRadius: '4px', fontWeight: 600 }}>
              Saves ${(project.professionalCost - project.estimatedCost).toLocaleString()} vs pro
            </span>
          )}
          {project.estimatedValueAdd > 0 && (
            <span style={{ fontSize: '0.73rem', background: '#dbeafe', color: '#1e40af', padding: '0.1rem 0.45rem', borderRadius: '4px', fontWeight: 600 }}>
              +${project.estimatedValueAdd.toLocaleString()} value
            </span>
          )}
        </div>
      </Link>

      <div className="card-actions">
        <button className="btn btn-secondary btn-sm" onClick={() => onEdit(project)}>Edit</button>
        <button className="btn btn-danger btn-sm" onClick={() => removeProject(project._id)}>Delete</button>
      </div>
    </div>
  );
};

export default memo(ProjectCard);
